import { useEffect, useState } from 'react'
import { Checkbox, Input, Select, Textarea } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'
import { ProfMailTemplate } from '@/models/profMailTemplate.ts'
import CopyableInput from '@/components/CopyableInput.tsx'
import { getAPIUrl, getDriveURL } from '@/controller/driveHelper.ts'
import { shareDownloadURL } from '@/controller/updateHelper.ts'
import { IconCheck, IconDeviceFloppy, IconDownload, IconLink, IconMail, IconPencil, IconSend, IconTrash } from '@tabler/icons-react'

const SAVED_PREFIX = 'saved:'
// Sentinel value used as the last dropdown option to enter "create template" mode.
const CREATE_OPTION = '__create__'

export interface EmailTemplate {
  id: string
  label: string
  subject: string
  message: string
}

export const emailTemplates: EmailTemplate[] = [
  {
    id: 'delivery',
    label: 'Lieferung an Kunden',
    subject: 'Eure Fotogalerie ist bereit',
    message: 'Hallo,\n\nhier findet ihr eure Fotogalerie mit allen Bildern. Über den untenstehenden Link könnt ihr die Galerie öffnen und die Fotos ansehen und herunterladen.\n\nViel Freude mit den Bildern!',
  },
  {
    id: 'selection',
    label: 'Auswahlgalerie',
    subject: 'Bitte trefft eure Bildauswahl',
    message: 'Hallo,\n\nhier findet ihr eure Auswahlgalerie. Bitte wählt über den untenstehenden Link die gewünschten Bilder aus. Eure Auswahl wird automatisch übermittelt – ihr müsst nichts weiter tun.\n\nVielen Dank!',
  },
  {
    id: 'custom',
    label: 'Eigener Betreff und Text',
    subject: '',
    message: '',
  },
]

interface ProjectEmailTabProps {
  project: ProfProject
}

export default function ProjectEmailTab({ project }: ProjectEmailTabProps) {
  const { sendProjectMail, getMailTemplates, createMailTemplate, updateMailTemplate, deleteMailTemplate } = useClient()
  const drive = project.drive

  const driveLink = getDriveURL(drive.id)

  const [selectedTemplateId, setSelectedTemplateId] = useState(emailTemplates[0].id)
  const [email, setEmail] = useState('')
  const [customSubject, setCustomSubject] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [sendCopy, setSendCopy] = useState(false)
  const [consent, setConsent] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isSent, setIsSent] = useState(false)

  // Account-bound saved templates loaded from the backend (newest first)
  const [savedTemplates, setSavedTemplates] = useState<ProfMailTemplate[]>([])
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  // Id of the saved template currently being edited (null = not editing)
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)

  useEffect(() => {
    getMailTemplates()
      .then(res => setSavedTemplates(res.data))
      .catch(() => { /* non-critical: fall back to built-in templates only */ })
  }, [getMailTemplates])

  const hardcodedTemplate = emailTemplates.find(t => t.id === selectedTemplateId)
  const isCustom = selectedTemplateId === 'custom'
  const isSaved = selectedTemplateId.startsWith(SAVED_PREFIX)
  const isCreatingTemplate = selectedTemplateId === CREATE_OPTION
  const isEditingTemplate = editingTemplateId !== null
  // "Template mode" = the send form is repurposed for creating or editing a template
  const isTemplateMode = isCreatingTemplate || isEditingTemplate
  const isEditable = isCustom || isSaved || isCreatingTemplate
  const selectedSavedId = isSaved ? selectedTemplateId.slice(SAVED_PREFIX.length) : null
  const currentSubject = isEditable ? customSubject : (hardcodedTemplate?.subject ?? '')
  const currentMessage = isEditable ? customMessage : (hardcodedTemplate?.message ?? '')

  function startEditingTemplate() {
    // The selected saved template's content is already loaded into customSubject/customMessage.
    if (selectedSavedId) setEditingTemplateId(selectedSavedId)
  }

  function handleTemplateChange(templateId: string) {
    // Carry the currently shown content over as a starting point when entering create mode.
    const carrySubject = currentSubject
    const carryMessage = currentMessage
    setEditingTemplateId(null)
    setSelectedTemplateId(templateId)
    if (templateId === CREATE_OPTION) {
      setCustomSubject(carrySubject)
      setCustomMessage(carryMessage)
      return
    }
    if (templateId === 'custom') return
    if (templateId.startsWith(SAVED_PREFIX)) {
      const saved = savedTemplates.find(t => `${SAVED_PREFIX}${t.id}` === templateId)
      if (saved) {
        setCustomSubject(saved.subject)
        setCustomMessage(saved.message)
      }
      return
    }
    const template = emailTemplates.find(t => t.id === templateId)!
    setCustomSubject(template.subject)
    setCustomMessage(template.message)
  }

  async function handleSaveTemplate() {
    const subject = currentSubject.trim()
    const message = currentMessage.trim()
    // The template name always mirrors the subject (kept in sync on every save).
    if (!subject || !message) return
    setIsSavingTemplate(true)
    try {
      let savedId: string
      if (isEditingTemplate && editingTemplateId) {
        await updateMailTemplate(editingTemplateId, { name: subject, subject, message })
        savedId = editingTemplateId
      } else {
        const createRes = await createMailTemplate({ name: subject, subject, message })
        savedId = createRes.data.id
      }
      // Reload the list so the dropdown is fresh, then select the saved template.
      const listRes = await getMailTemplates()
      setSavedTemplates(listRes.data)
      setEditingTemplateId(null)
      setSelectedTemplateId(`${SAVED_PREFIX}${savedId}`)
      const saved = listRes.data.find(t => t.id === savedId)
      if (saved) {
        setCustomSubject(saved.subject)
        setCustomMessage(saved.message)
      }
      toast('Vorlage gespeichert', { closeButton: true, autoClose: 2000, type: 'success' })
    } catch {
      toast('Fehler beim Speichern der Vorlage', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsSavingTemplate(false)
    }
  }

  async function handleDeleteTemplate() {
    if (!selectedSavedId) return
    try {
      await deleteMailTemplate(selectedSavedId)
      setSavedTemplates(prev => prev.filter(t => t.id !== selectedSavedId))
      setEditingTemplateId(null)
      setSelectedTemplateId(emailTemplates[0].id)
      toast('Vorlage gelöscht', { closeButton: true, autoClose: 2000, type: 'success' })
    } catch {
      toast('Fehler beim Löschen der Vorlage', { closeButton: true, autoClose: 3000, type: 'error' })
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isTemplateMode) {
      handleSaveTemplate()
    } else {
      handleSend()
    }
  }

  async function handleSend() {
    setIsSending(true)
    try {
      await sendProjectMail(project.id, {
        email: email.trim(),
        subject: currentSubject.trim(),
        message: currentMessage.trim(),
        send_copy: sendCopy,
      })
      setIsSent(true)
    } catch {
      toast('Fehler beim Senden der E-Mail', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsSending(false)
    }
  }

  async function onQRCodeDownloadClick() {
    const qrCodeUrl = getAPIUrl(`api/drive/${drive.id}/download_qr_code/`)
    await shareDownloadURL(qrCodeUrl, 'FridaySnap-QR-Code.png', () => {})
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Left side */}
      <div className="flex flex-col gap-6">
        {/* Link */}
        <section className="bg-neutral p-5">
          <div className="flex items-center gap-2 mb-3">
            <IconLink size={20} className="text-primary" />
            <h3 className="font-semibold">Galerie-Link</h3>
          </div>
          <CopyableInput value={driveLink} />
        </section>

        {/* QR Code */}
        <section className="bg-neutral p-5">
          <div className="flex items-center gap-2 mb-3">
            <IconDownload size={20} className="text-primary" />
            <h3 className="font-semibold">QR-Code</h3>
          </div>
          {drive.qr_code_image ? (
            <div className="flex items-center gap-4">
              <img crossOrigin="anonymous" src={drive.qr_code_image} className="w-28 rounded" alt="QR Code" />
              <Button color="primary" size="sm" variant="outline" onClick={onQRCodeDownloadClick}>
                <IconDownload size={18} />
                Herunterladen
              </Button>
            </div>
          ) : (
            <p className="opacity-60">Kein QR-Code vorhanden.</p>
          )}
        </section>
      </div>

      {/* Right side */}
      <div className="flex flex-col gap-6">
        {/* Email form */}
        <section className="bg-neutral p-5">
          <div className="flex items-center gap-2 mb-3">
            {isTemplateMode ? <IconDeviceFloppy size={20} className="text-primary" /> : <IconSend size={20} className="text-primary" />}
            <h3 className="font-semibold">{isEditingTemplate ? 'Vorlage anpassen' : isCreatingTemplate ? 'Vorlage erstellen' : 'Per E-Mail versenden'}</h3>
          </div>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Vorlage</span>
              </div>
              <div className="flex items-center gap-2">
                {/* @ts-ignore */}
                <Select
                  className="flex-1"
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.currentTarget.value)}
                >
                  {emailTemplates.map(t => (
                    <Select.Option key={t.id} value={t.id}>{t.label}</Select.Option>
                  ))}
                  {savedTemplates.length > 0 && (
                    <optgroup label="Meine Vorlagen">
                      {savedTemplates.map(t => (
                        <option key={t.id} value={`${SAVED_PREFIX}${t.id}`}>{t.name}</option>
                      ))}
                    </optgroup>
                  )}
                  <option value={CREATE_OPTION}>+ Eigene Vorlage erstellen</option>
                </Select>
                {isSaved && !isEditingTemplate && (
                  <>
                    <Button
                      type="button"
                      color="ghost"
                      size="sm"
                      shape="square"
                      onClick={startEditingTemplate}
                      title="Vorlage anpassen"
                    >
                      <IconPencil size={18} className="text-primary" />
                    </Button>
                    <Button
                      type="button"
                      color="ghost"
                      size="sm"
                      shape="square"
                      onClick={handleDeleteTemplate}
                      title="Vorlage löschen"
                    >
                      <IconTrash size={18} className="text-error" />
                    </Button>
                  </>
                )}
              </div>
            </label>

            {!isTemplateMode && (
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Empfänger <span className="text-error">*</span></span>
                </div>
                <Input
                  type="email"
                  placeholder="kunde@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setIsSent(false) }}
                  required
                />
              </label>
            )}

            {isEditable && (
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Betreff <span className="text-error">*</span></span>
                </div>
                <Input
                  placeholder="Betreff eingeben..."
                  value={customSubject}
                  onChange={e => setCustomSubject(e.target.value)}
                  required
                />
              </label>
            )}

            {isEditable && (
              <label className="form-control w-full">
                <div className="label">
                  <span className="label-text">Nachricht <span className="text-error">*</span></span>
                </div>
                <Textarea
                  className="w-full"
                  rows={5}
                  placeholder="Nachricht eingeben..."
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  required
                />
              </label>
            )}

            {!isTemplateMode && (
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={consent}
                    onChange={() => setConsent(!consent)}
                    required
                  />
                  <span className="text-sm">Ich bestätige, dass ich diese E-Mail senden darf <span className="text-error">*</span></span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    color="primary"
                    checked={sendCopy}
                    onChange={() => setSendCopy(!sendCopy)}
                  />
                  <span className="text-sm">Kopie an mich senden</span>
                </label>
              </div>
            )}

            {isTemplateMode ? (
              <Button
                type="submit"
                color="primary"
                loading={isSavingTemplate}
                disabled={isSavingTemplate || !currentSubject.trim() || !currentMessage.trim()}
              >
                <IconDeviceFloppy size={18} />
                Vorlage speichern
              </Button>
            ) : isSent ? (
              <div className="flex items-center justify-center gap-2 h-12 text-success font-medium">
                <IconCheck size={18} />
                E-Mail erfolgreich versendet
              </div>
            ) : (
              <Button
                type="submit"
                color="primary"
                loading={isSending}
                disabled={isSending}
              >
                <IconMail size={18} />
                E-Mail senden
              </Button>
            )}
          </form>
        </section>

        {/* Email preview */}
        {currentSubject && currentMessage && (
          <section className="bg-neutral p-5">
            <div className="flex items-center gap-2 mb-3">
              <IconMail size={20} className="text-primary" />
              <h3 className="font-semibold">Vorschau</h3>
            </div>
            <EmailPreview subject={currentSubject} message={currentMessage} />
          </section>
        )}
      </div>
    </div>
  )
}

function EmailPreview({ subject, message }: { subject: string, message: string }) {
  return (
    <div className="border border-base-300 overflow-hidden bg-secondary text-secondary-content">
      {/* Email header */}
      <div className="bg-secondary-focus px-5 py-3 border-b border-base-300">
        <p className="text-xs opacity-60">Betreff</p>
        <p className="font-semibold text-sm">{subject}</p>
      </div>

      {/* Email body */}
      <div className="px-5 py-5 flex flex-col gap-5">
        <p className="text-sm whitespace-pre-line leading-relaxed">{message}</p>

        {/* CTA button (visual only) */}
        <div className="text-center">
          <span className="inline-block bg-primary text-neutral font-semibold text-sm px-6 py-3 rounded cursor-default">
            Galerie öffnen
          </span>
        </div>
      </div>
    </div>
  )
}
